import type { InvoiceStatus } from '@hero/schema';

type InvoiceStatusConfig = Readonly<
  Record<
    InvoiceStatus,
    Readonly<{
      /** can make a payment on during this status. */
      canMakePayment: boolean;
      /** can upload an actual invoice proof on this invoice during this status.
       *
       * This will be mainly used for carriers to upload an actual VAT invoice.
       */
      canUploadInvoice: boolean;
      /** whether the invoice can be closed or not */
      canClose: boolean;
    }>
  >
>;

export const statusConfig: InvoiceStatusConfig = {
  // SaaS-era: VAT upload is allowed on any non-terminal invoice
  // (including `on_going`). The legacy "coverage period must end
  // first" gate was a broker-era artefact — sellers in the
  // bilateral model can attach the VAT PDF the moment the
  // invoice is created.
  not_paid: { canMakePayment: true, canUploadInvoice: true, canClose: false },
  partially_paid: { canMakePayment: true, canUploadInvoice: true, canClose: false },
  paid: { canMakePayment: false, canUploadInvoice: false, canClose: false },
  pending: { canMakePayment: false, canUploadInvoice: true, canClose: false },
  cancelled: { canMakePayment: false, canUploadInvoice: false, canClose: false },
  on_going: { canMakePayment: false, canUploadInvoice: true, canClose: true },
} as const;

/**
 * Get the status config for a given invoice status.
 *
 * @example
 * ```ts
 * const { canMakePayment, canUploadInvoice } = getInvoiceStatusConfig('not_paid');
 * ```
 */
export function getInvoiceStatusConfig(status: InvoiceStatus) {
  return statusConfig[status];
}

export const invoice = {
  /** the status config of the invoice */
  statusConfig,
  /** get the status config for a given invoice status */
  getInvoiceStatusConfig,
} as const;
